import { Link } from "@/lib/router-compat";
import { ExternalLink, ArrowRight } from "lucide-react";
import { AUTHORITY_CHAIN } from "@/lib/entityDefinition";

/**
 * Authority chain visual — shows the relationship from Stanford research
 * through FDA clearance, Magnus Medical, Verified SAINT® Provider, and
 * finally Neuro Wellness as the treatment delivery point. Renders as
 * semantic HTML with outbound citations so AI/search systems can trace
 * the provenance of SAINT® claims.
 */
export const AuthorityChain = () => (
  <section className="bg-background py-16 sm:py-20">
    <div className="container-px mx-auto max-w-4xl px-4">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-accent">
          Provenance & Verification
        </span>
        <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          How SAINT® reaches patients
        </h2>
        <p className="mt-4 text-base text-foreground/70 max-w-2xl mx-auto leading-relaxed">
          SAINT® TMS follows a clear chain — from Stanford research through FDA
          clearance, Magnus Medical manufacturing, and verified provider
          certification — before reaching patients at Neuro Wellness TMS Centers
          of America.
        </p>
      </div>

      <ol className="space-y-4">
        {AUTHORITY_CHAIN.map((step, i) => {
          const isInternal = step.url?.startsWith("/");
          const isExternal = step.url && !isInternal;
          return (
            <li key={i}>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 font-headings text-sm font-bold text-accent">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-headings text-base font-bold text-primary sm:text-lg">
                    {step.label}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground/80 leading-relaxed">
                    {step.description}
                  </p>
                  {step.url && (
                    <div className="mt-2">
                      {isInternal ? (
                        <Link
                          to={step.url}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-accent underline underline-offset-2 hover:text-primary"
                        >
                          Learn more
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      ) : (
                        <a
                          href={step.url}
                          target="_blank"
                          rel="noopener"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-accent underline underline-offset-2 hover:text-primary"
                        >
                          Visit source
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  </section>
);

export default AuthorityChain;
